import React, { useState, useRef } from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import { formatDistanceToNow } from 'date-fns';
import { fr } from 'date-fns/locale';
import { NoteContent } from '@europrocurement/flexy-components/components/atoms/NoteContent/NoteContent';
import { useNotePermissions } from '@europrocurement/flexy-components/hooks/useNotePermissions';
import { ActivityProps } from '@europrocurement/flexy-components/types/ActivityHistoryTypes';
import { useContextMenu } from '@europrocurement/l2d-hooks/src/hooks/useContextMenu';
import { NoteContextMenu } from './NoteContextMenu';
import { CustomAvatar } from '../../CustomAvatar';
import { NoteToolbox } from '../../NoteToolbox/NoteToolbox';
import { NoteEditedIndicator } from '../../NoteEditedIndicator/NoteEditedIndicator';

export type ActivityItemProps = {
    activity: ActivityProps;
    index: number;
    onEdit: (noteId: string, content: string, highlighted: boolean) => void;
    onDelete: (noteId: string) => void;
};

export const ActivityItem: React.FunctionComponent<ActivityItemProps> = function ({
    activity,
    index,
    onEdit,
    onDelete,
}) {
    const [isHovered, setIsHovered] = useState(false);
    const noteContentRef = useRef<HTMLDivElement>(null);
    const theme = useTheme();

    const { contextMenu, openContextMenu, closeContextMenu } = useContextMenu();
    const notePermissions = useNotePermissions(activity.userId || '');

    const isNoteEdited =
        activity.type === 'note' &&
        activity.updatedAt &&
        activity.updatedAt.getTime() !== activity.createdAt.getTime();

    // Désactiver la toolbox normale si le menu contextuel est ouvert
    const shouldShowToolbox =
        activity.type === 'note' &&
        isHovered &&
        notePermissions.hasAnyPermission &&
        !contextMenu.open; // Ajout de cette condition

    const timeAgo = formatDistanceToNow(activity.createdAt, {
        addSuffix: true,
        locale: fr,
    });

    const handleEditNote = () => {
        if (activity.type === 'note') {
            onEdit(activity.id, activity.content || '', activity.noteType === 'important');
        }
    };

    const handleDeleteNote = () => {
        if (activity.type === 'note') {
            onDelete(activity.id);
        }
    };

    const handleNoteContextMenu = (event: React.MouseEvent) => {
        if (activity.type === 'note' && notePermissions.hasAnyPermission) {
            openContextMenu(event);
        }
    };

    const handleItemClick = () => {
        // Fermer le menu contextuel si ouvert
        if (contextMenu.open) {
            closeContextMenu();
        }
    };

    return (
        <>
            <Box
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
                sx={{
                    ...(index > 0 && {
                        borderTop: '1px solid',
                        borderColor: 'divider',
                        paddingTop: '8px',
                        marginTop: '8px',
                    }),
                    cursor: 'pointer',
                    position: 'relative',
                }}
                onClick={handleItemClick}
            >
                <Box sx={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                    <CustomAvatar
                        firstName={activity.userFirstName}
                        lastName={activity.userLastName || 'Inconnu'}
                        keycloakId={activity.userId}
                        sx={{ width: 20, height: 20, fontSize: '8px' }}
                    />

                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        {/* Informations utilisateur et date */}
                        <Box sx={{ display: 'flex', gap: '4px', marginBottom: '4px' }}>
                            <Typography
                                variant="l2dbodys"
                                sx={{
                                    color: 'text.secondary',
                                    fontSize: '10px',
                                    fontWeight: 600,
                                }}
                            >
                                {activity.userFirstName} {activity.userLastName},
                            </Typography>
                            <Typography
                                variant="l2dbodyxs"
                                sx={{
                                    color: 'text.secondary',
                                    fontSize: '9px',
                                    fontWeight: 500,
                                }}
                            >
                                {timeAgo}
                            </Typography>
                        </Box>

                        {activity.type === 'note' ? (
                            <Box
                                ref={noteContentRef}
                                onContextMenu={handleNoteContextMenu}
                                sx={{
                                    padding: '8px 12px',
                                    backgroundColor:
                                        activity.noteType === 'important'
                                            ? theme.palette.primary.light
                                            : theme.palette.background.paper,
                                    border: '1px solid',
                                    borderColor:
                                        activity.noteType === 'important'
                                            ? theme.palette.primary.main
                                            : 'divider',
                                    borderRadius: '8px',
                                    position: 'relative',
                                    width: 'fit-content',
                                    maxWidth: '100%',
                                    paddingBottom: isNoteEdited ? '20px' : '8px',
                                    // Indicateur visuel si menu contextuel ouvert
                                    ...(contextMenu.open && {
                                        backgroundColor:
                                            activity.noteType === 'important'
                                                ? theme.palette.primary.main
                                                : theme.palette.action.selected,
                                    }),
                                }}
                            >
                                <NoteContent
                                    content={activity.content || ''}
                                    sx={{
                                        color: theme.palette.text.primary,
                                        fontSize: '10px',
                                        lineHeight: 1.3,
                                        '& p': { margin: 0 },
                                        '& p:not(:last-child)': { marginBottom: '4px' },
                                    }}
                                />

                                {/* Indicateur "modifiée" */}
                                {isNoteEdited && (
                                    <NoteEditedIndicator
                                        updatedAt={activity.updatedAt!}
                                        updatedByFirstName={activity.updatedByFirstName!}
                                        updatedByLastName={activity.updatedByLastName!}
                                    />
                                )}

                                <NoteToolbox
                                    visible={shouldShowToolbox}
                                    onEdit={handleEditNote}
                                    onDelete={handleDeleteNote}
                                    parentRef={noteContentRef}
                                />
                            </Box>
                        ) : (
                            <Typography
                                variant="caption"
                                sx={{
                                    color: 'text.primary',
                                    fontSize: '10px',
                                }}
                            >
                                {activity.description}
                            </Typography>
                        )}
                    </Box>
                </Box>
            </Box>

            {/* Menu contextuel pour les notes */}
            {activity.type === 'note' && (
                <NoteContextMenu
                    contextMenu={contextMenu}
                    onClose={closeContextMenu}
                    onEdit={handleEditNote}
                    onDelete={handleDeleteNote}
                    canEdit={notePermissions.canEdit}
                    canDelete={notePermissions.canDelete}
                    noteContent={activity.content || ''}
                    createdBy={`${activity.userFirstName} ${activity.userLastName}`}
                    createdAt={activity.createdAt}
                    isImportant={activity.noteType === 'important'}
                />
            )}
        </>
    );
};
